/**
 * Recharge Widget V2 Integration
 * Simple integration that updates the product variant/selling plan based on Recharge widget selection
 */

class RechargeWidgetV2Integration {
  constructor() {
    this.widget = null;
    this.propertyPollInterval = null;
    this.init();
  }

  init() {
    // Wait for widget to load
    const checkWidget = () => {
      this.widget = document.querySelector('recharge-subscription-widget');
      if (this.widget) {
        this.setupIntegration();
      } else {
        setTimeout(checkWidget, 100);
      }
    };
    checkWidget();
  }

  setupIntegration() {
    // Find buttons
    const addToCartButton = document.querySelector('[data-add-to-cart-button]');
    const rechargeButton = document.querySelector('[data-recharge-button]');
    const productForm = document.querySelector('product-form form');
    const hiddenForm = rechargeButton ? 
      document.querySelector(`#recharge-hidden-form-${rechargeButton.getAttribute('data-product-id')}`) : null;

    // Setup button handlers
    if (addToCartButton && productForm) {
      addToCartButton.addEventListener('click', (e) => this.handleAddToCart(e, productForm));
    }
    
    if (rechargeButton && hiddenForm) {
      rechargeButton.addEventListener('click', (e) => this.handleAddToCart(e, hiddenForm));
    }

    // Watch for selection changes
    this.watchWidgetSelection();
  }

  watchWidgetSelection() {
    if (!this.widget) return;

    let lastSelection = null;
    let lastWidgetProperties = null;
    
    // Watch for clicks in widget
    const checkSelection = () => {
      // Check widget properties first (they might update faster than DOM)
      const currentProperties = {
        selectedSellingPlanId: this.widget.selectedSellingPlanId,
        selectedVariantId: this.widget.selectedVariantId,
        selectedSellingPlan: this.widget.getAttribute('selected-selling-plan'),
        selectedVariant: this.widget.getAttribute('selected-variant-id')
      };
      
      const propertiesChanged = JSON.stringify(currentProperties) !== JSON.stringify(lastWidgetProperties);
      if (propertiesChanged) {
        lastWidgetProperties = currentProperties;
      }
      
      const selection = this.getSelection();
      
      if (selection && selection.variantId) {
        // Only update if selection actually changed
        const selectionKey = `${selection.variantId}-${selection.sellingPlanId || 'none'}-${selection.quantity || 'none'}`;
        const lastKey = lastSelection ? `${lastSelection.variantId}-${lastSelection.sellingPlanId || 'none'}-${lastSelection.quantity || 'none'}` : null;
        
        if (selectionKey !== lastKey) {
          if (window.posthog && lastSelection !== null) {
            const prevIsSubscription = !!lastSelection.sellingPlanId;
            const nowIsSubscription = !!selection.sellingPlanId;
            if (prevIsSubscription !== nowIsSubscription) {
              posthog.capture('purchase_type_selected', {
                purchase_type: nowIsSubscription ? 'subscription' : 'one_time',
                previous_type: prevIsSubscription ? 'subscription' : 'one_time',
                product_name: window.globalVariables?.product?.title || null,
                variant_id: String(selection.variantId),
                selling_plan_id: selection.sellingPlanId ? String(selection.sellingPlanId) : null,
              });
            }
          }
          lastSelection = selection;
          this.updateFormFields(selection);
          this.updateButtonState(selection);
        }
      }
    };
    
    // Poll widget properties periodically (in case they update without DOM changes)
    this.propertyPollInterval = setInterval(() => {
      const currentProperties = {
        selectedSellingPlanId: this.widget.selectedSellingPlanId,
        selectedVariantId: this.widget.selectedVariantId,
        selectedSellingPlan: this.widget.getAttribute('selected-selling-plan'),
        selectedVariant: this.widget.getAttribute('selected-variant-id')
      };
      
      if (JSON.stringify(currentProperties) !== JSON.stringify(lastWidgetProperties)) {
        lastWidgetProperties = currentProperties;
        checkSelection();
      }
    }, 500);

    // Check on widget clicks
    this.widget.addEventListener('click', () => {
      setTimeout(checkSelection, 300);
    }, true);

    // Check on input changes
    this.widget.addEventListener('change', () => {
      setTimeout(checkSelection, 300);
    }, true);

    // Watch for DOM changes in shadow DOM
    if (this.widget.shadowRoot) {
      this.widget.shadowRoot.addEventListener('click', () => {
        setTimeout(checkSelection, 300);
      }, true);
      
      this.widget.shadowRoot.addEventListener('change', () => {
        setTimeout(checkSelection, 300);
      }, true);
    }

    // Watch for DOM mutations - also watch nested shadow DOMs
    const setupObserver = (root) => {
      const observer = new MutationObserver((mutations) => {
        let shouldCheck = false;
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && 
              (mutation.attributeName === 'checked' || 
               mutation.attributeName === 'data-selected' ||
               mutation.attributeName === 'class')) {
            shouldCheck = true;
          }
          if (mutation.type === 'childList') {
            shouldCheck = true;
            // When new nodes are added, check if they have shadow roots
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1 && node.shadowRoot) {
                setupObserver(node.shadowRoot);
              }
            });
          }
        });
        if (shouldCheck) {
          setTimeout(checkSelection, 300);
        }
      });
      
      observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-selected', 'class', 'checked', 'aria-selected']
      });
      
      // Also watch any existing nested shadow DOMs
      const allElements = root.querySelectorAll('*');
      allElements.forEach((el) => {
        if (el.shadowRoot) {
          setupObserver(el.shadowRoot);
        }
      });
    };
    
    // Watch regular DOM
    setupObserver(this.widget);
    
    // Watch shadow DOM
    if (this.widget.shadowRoot) {
      setupObserver(this.widget.shadowRoot);
    }

    // Initial check
    setTimeout(checkSelection, 500);
    setTimeout(checkSelection, 2000);
  }

  updateFormFields(selection) {
    if (!selection || !selection.variantId) return;
    
    // Update product form if it exists
    const productForm = document.querySelector('product-form form');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"], input[name="variant_id"]');
      if (variantInput) {
        variantInput.value = selection.variantId;
      }
    }

    // Update hidden form for standalone button if it exists
    const rechargeButton = document.querySelector('[data-recharge-button]');
    if (rechargeButton) {
      const productId = rechargeButton.getAttribute('data-product-id');
      const hiddenForm = document.querySelector(`#recharge-hidden-form-${productId}`);
      
      if (hiddenForm) {
        const variantInput = hiddenForm.querySelector('input[name="id"], input[name="variant_id"]');
        if (variantInput) {
          variantInput.value = selection.variantId;
        }

        // Handle selling plan - either set it or remove it for one-time purchases
        let sellingPlanInput = hiddenForm.querySelector('input[name="selling_plan"], input[name="selling_plan_id"]');
        
        if (selection.sellingPlanId && selection.sellingPlanId !== '' && selection.sellingPlanId !== null) {
          // Subscription selected - set or create selling plan input
          if (!sellingPlanInput) {
            sellingPlanInput = document.createElement('input');
            sellingPlanInput.type = 'hidden';
            sellingPlanInput.name = 'selling_plan';
            hiddenForm.appendChild(sellingPlanInput);
          }
          sellingPlanInput.value = selection.sellingPlanId;
        } else {
          // One-time purchase - remove selling plan input if it exists
          if (sellingPlanInput) {
            sellingPlanInput.remove();
          }
        }
      }
    }
  }

  // Recursively search through shadow DOMs
  findInShadowDOM(root, selector) {
    if (!root) return null;
    
    // Try direct query
    let found = root.querySelector(selector);
    if (found) return found;
    
    // Search in nested shadow DOMs
    const allElements = root.querySelectorAll('*');
    for (const el of allElements) {
      if (el.shadowRoot) {
        found = this.findInShadowDOM(el.shadowRoot, selector);
        if (found) return found;
      }
    }
    
    return null;
  }

  // Recursively find all elements matching selector in shadow DOMs
  findAllInShadowDOM(root, selector) {
    const results = [];
    if (!root) return results;
    
    // Add direct matches
    const directMatches = root.querySelectorAll(selector);
    results.push(...directMatches);
    
    // Search in nested shadow DOMs
    const allElements = root.querySelectorAll('*');
    for (const el of allElements) {
      if (el.shadowRoot) {
        const nestedResults = this.findAllInShadowDOM(el.shadowRoot, selector);
        results.push(...nestedResults);
      }
    }
    
    return results;
  }

  getSelection() {
    if (!this.widget) return null;

    // Start with default variant ID
    let variantId = this.widget.getAttribute('default-variant-id');
    let sellingPlanId = null;
    
    // Method 1: Try to get selection from widget properties/attributes
    // Check if one-time is selected first
    if (this.widget.getAttribute('selected-purchase-type') === 'onetime' ||
        this.widget.selectedPurchaseType === 'onetime') {
      sellingPlanId = null; // Explicitly null for one-time
    } else if (this.widget.selectedSellingPlanId) {
      sellingPlanId = this.widget.selectedSellingPlanId;
    } else if (this.widget.getAttribute('selected-selling-plan')) {
      const selectedPlan = this.widget.getAttribute('selected-selling-plan');
      // Only set if it's not empty (empty means one-time)
      sellingPlanId = selectedPlan && selectedPlan !== '' ? selectedPlan : null;
    }
    
    if (this.widget.selectedVariantId) {
      variantId = this.widget.selectedVariantId;
    } else if (this.widget.getAttribute('selected-variant-id')) {
      variantId = this.widget.getAttribute('selected-variant-id');
    }
    
    // Method 2: Try to find in shadow DOM (recursive search)
    if (!sellingPlanId) {
      let selectedOption = null;
      
      if (this.widget.shadowRoot) {
        selectedOption = this.findInShadowDOM(this.widget.shadowRoot, 'input:checked') ||
                         this.findInShadowDOM(this.widget.shadowRoot, 'input[type="radio"]:checked');
        
        if (!selectedOption) {
          const allRadios = this.findAllInShadowDOM(this.widget.shadowRoot, 'input[type="radio"]');
          allRadios.forEach(radio => {
            if (radio.checked) {
              selectedOption = radio;
            }
          });
        }
      }
      
      if (selectedOption) {
        // Check if this is a one-time purchase option
        const isOneTime = selectedOption.getAttribute('data-type') === 'onetime' ||
                         selectedOption.closest('[data-purchase_type]')?.getAttribute('data-purchase_type') === 'onetime' ||
                         selectedOption.value === '' ||
                         selectedOption.value === null;
        
        if (isOneTime) {
          // Explicitly set to null for one-time purchases
          sellingPlanId = null;
        } else {
          const isQuantityUpsell = selectedOption.name === 'quantityUpsellOption' || 
                                   selectedOption.name === 'subscriptionOption';
          
          if (isQuantityUpsell && selectedOption.value) {
            sellingPlanId = selectedOption.value;
          } else if (selectedOption.value) {
            sellingPlanId = selectedOption.value;
          }
        }
      }
    }
    
    // Method 3: Don't auto-select first plan as fallback
    // If no selling plan is found, it means either:
    // 1. One-time purchase was selected (sellingPlanId should be null)
    // 2. No selection has been made yet (sellingPlanId should remain null)
    // We don't want to force a subscription by default
    
    // Extract quantity from SubscriptionWidgetConfig
    let quantity = null;
    if (sellingPlanId && variantId && window.SubscriptionWidgetConfig?.formattedPrices?.[variantId]) {
      const variantPrices = window.SubscriptionWidgetConfig.formattedPrices[variantId];
      if (variantPrices.sellingPlans?.[sellingPlanId]) {
        const planData = variantPrices.sellingPlans[sellingPlanId];
        
        // Check if there's a quantity property
        if (planData.quantity) {
          quantity = planData.quantity;
        } else if (planData.quantityUpsell) {
          // For quantity upsells, try to infer quantity from the plan name or calculate from pricing
          const plan = window.SubscriptionWidgetConfig.shopifyPlans?.find(p => p.id == sellingPlanId);
          if (plan) {
            // Try to extract quantity from plan name (e.g., "3 month supply" = 3)
            const quantityMatch = plan.name.match(/(\d+)\s*(month|supply|pack|quantity)/i);
            if (quantityMatch) {
              quantity = parseInt(quantityMatch[1], 10);
            } else {
              // Try to calculate from pricing (totalDiscountedPrice / unit price)
              const unitPrice = parseFloat(variantPrices.unit?.replace(/[^0-9.]/g, '') || variantPrices.discounted?.replace(/[^0-9.]/g, ''));
              const totalPrice = parseFloat(planData.quantityUpsell?.totalDiscountedPrice?.replace(/[^0-9.]/g, ''));
              if (unitPrice && totalPrice && unitPrice > 0) {
                quantity = Math.round(totalPrice / unitPrice);
              }
            }
          }
        }
      }
    }
    
    // Check widget for quantity property
    if (!quantity && this.widget.selectedQuantity) {
      quantity = this.widget.selectedQuantity;
    } else if (!quantity && this.widget.getAttribute('selected-quantity')) {
      quantity = parseInt(this.widget.getAttribute('selected-quantity'), 10);
    }

    return { variantId, sellingPlanId, quantity: quantity || null };
  }

  updateButtonState(selection) {
    const addToCartButton = document.querySelector('[data-add-to-cart-button]');
    const rechargeButton = document.querySelector('[data-recharge-button]');

    // Enable buttons
    if (addToCartButton) addToCartButton.disabled = false;
    if (rechargeButton) rechargeButton.disabled = false;

    // Update button label based on purchase type
    this.updateButtonText(selection);
  }

  /**
   * Set button text to "Add to cart" (one-time) or "Subscribe & save X%" (subscription).
   * Targets both [data-add-to-cart-button] (main form button) and [data-recharge-button]
   * (standalone recharge block button) plus any sticky [data-atc-out] buttons.
   * Called every time the widget selection changes.
   */
  updateButtonText(selection) {
    const isSubscription = !!(
      selection &&
      selection.sellingPlanId &&
      selection.sellingPlanId !== '' &&
      selection.sellingPlanId !== null
    );

    let text;
    if (isSubscription) {
      const productId =
        document.querySelector('[data-recharge-button]')?.getAttribute('data-product-id') ||
        document.querySelector('[data-add-to-cart-button]')?.getAttribute('data-current-product-id');
      const discount = this.getSellingPlanDiscount(selection.sellingPlanId, productId);
      text = discount ? `Subscribe & save ${discount}%` : 'Subscribe & save';
    } else {
      text = window.variantStrings?.addToCart || 'Add to cart';
    }

    // Main add-to-cart button (product form)
    const addToCartBtn = document.querySelector('[data-add-to-cart-button]');
    if (addToCartBtn) {
      const span = addToCartBtn.querySelector('.add-text');
      if (span) span.innerHTML = text;
    }

    // Standalone recharge block button
    const rechargeBtn = document.querySelector('[data-recharge-button]');
    if (rechargeBtn) {
      const span = rechargeBtn.querySelector('.add-text');
      if (span) span.innerHTML = text;
    }

    // Sticky ATC buttons
    document.querySelectorAll('[data-atc-out]')?.forEach(ele => {
      const span = ele.querySelector('.add-text');
      if (span) span.innerHTML = text;
    });
  }

  /**
   * Look up the percentage discount for a given selling plan ID.
   * Primary source: the JSON script tag embedded by the Liquid template.
   * Fallback: SubscriptionWidgetConfig (available in some Recharge setups).
   *
   * @param {string|number} sellingPlanId
   * @param {string|number} productId
   * @returns {number|null} discount percentage (e.g. 20) or null if not found
   */
  getSellingPlanDiscount(sellingPlanId, productId) {
    // Collect all selling-plan-discounts script tags on page (try specific product first)
    const scriptIds = productId ? [`selling-plan-discounts-${productId}`] : [];
    document.querySelectorAll('[id^="selling-plan-discounts-"]').forEach(el => {
      if (!scriptIds.includes(el.id)) scriptIds.push(el.id);
    });

    for (const id of scriptIds) {
      const scriptEl = document.getElementById(id);
      if (scriptEl) {
        try {
          const data = JSON.parse(scriptEl.textContent);
          const discount = data[String(sellingPlanId)];
          if (discount && discount > 0) return discount;
        } catch (e) {
          // malformed JSON — try next
        }
      }
    }

    // Fallback: SubscriptionWidgetConfig.shopifyPlans (Recharge v2 runtime config)
    if (window.SubscriptionWidgetConfig?.shopifyPlans) {
      const plan = window.SubscriptionWidgetConfig.shopifyPlans
        .find(p => String(p.id) === String(sellingPlanId));
      if (plan?.price_adjustments?.[0]?.value_type === 'percentage') {
        return Math.round(plan.price_adjustments[0].value);
      }
    }

    return null;
  }

  handleAddToCart(event, form) {
    event.preventDefault();
    
    const selection = this.getSelection();
    if (!selection || !selection.variantId) {
      console.warn('[Recharge Widget] Add to cart called with no variant selected');
      if (typeof this.showError === 'function') {
        this.showError('Add to Cart', 'Please select an option.');
      }
      return;
    }

    // Update form with selection
    const variantInput = form.querySelector('input[name="id"], input[name="variant_id"]');
    if (variantInput) {
      variantInput.value = selection.variantId;
    }

    // Handle selling plan - either set it or remove it for one-time purchases
    let sellingPlanInput = form.querySelector('input[name="selling_plan"], input[name="selling_plan_id"]');
    
    if (selection.sellingPlanId && selection.sellingPlanId !== '' && selection.sellingPlanId !== null) {
      // Subscription selected - set or create selling plan input
      if (!sellingPlanInput) {
        sellingPlanInput = document.createElement('input');
        sellingPlanInput.type = 'hidden';
        sellingPlanInput.name = 'selling_plan';
        form.appendChild(sellingPlanInput);
      }
      sellingPlanInput.value = selection.sellingPlanId;
    } else {
      // One-time purchase - remove selling plan input if it exists
      if (sellingPlanInput) {
        sellingPlanInput.remove();
      }
    }

    // Get quantity from selection, form, or default to 1
    let quantity = 1;
    if (selection.quantity) {
      quantity = parseInt(selection.quantity, 10) || 1;
    } else {
      const quantityInput = form.querySelector('input[name="quantity"]');
      quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
    }

    // Prepare cart data
    const cartItem = {
      id: String(selection.variantId),
      quantity: quantity
    };

    // Only add selling_plan if it's actually a subscription (not one-time)
    if (selection.sellingPlanId && selection.sellingPlanId !== '' && selection.sellingPlanId !== null) {
      cartItem.selling_plan = String(selection.sellingPlanId);
    }

    // Audience attribution — inject from session if available
    var _attr = (typeof window.bubsLPAttribution !== 'undefined') ? window.bubsLPAttribution : {};
    if (_attr.topic || _attr.gender) {
      cartItem.properties = {
        _lp_topic: _attr.topic || '',
        _lp_gender: _attr.gender || '',
        _lp_source: _attr.source || ''
      };
    }

    // Submit to cart
    this.submitToCart(cartItem, event.target);
  }

  submitToCart(cartItem, submitButton) {
    submitButton.disabled = true;
    submitButton.classList.add('loading');

    const body = JSON.stringify({
      items: [cartItem],
      sections: `${((window.globalVariables?.template == 'cart') ? 'template-cart' : 'component-cart-drawer')},header`
    });

    fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: body
    })
    .then(async response => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ errors: 'Cart Error' }));
        const cartErr = new Error(JSON.stringify(errorData));
        cartErr.httpStatus = response.status;
        throw cartErr;
      }
      return response.json();
    })
    .then(data => {
      if (data.errors) {
        this.showError('Add to Cart', data.errors);
        window.bubsTrackCartError?.({
          error_message: Array.isArray(data.errors)
            ? data.errors.join(', ') : String(data.errors),
          is_cart_errors: true,
          variant_id: cartItem.id,
          quantity: cartItem.quantity,
          purchase_type: cartItem.selling_plan ? 'subscription' : 'one_time',
          selling_plan_id: cartItem.selling_plan || null,
          source: 'recharge_widget',
        });
        return;
      }
      this.handleCartSuccess(data);
    })
    .catch(error => {
      console.error('[Recharge Widget] Cart add request failed', error);
      let errorMessage = 'Failed to add to cart';
      try {
        const errorData = JSON.parse(error.message);
        errorMessage = errorData.errors || errorData.message || errorMessage;
        if (Array.isArray(errorMessage)) {
          errorMessage = errorMessage.join(', ');
        }
      } catch (e) {
        if (error.message) errorMessage = error.message;
      }
      this.showError('Add to Cart', errorMessage);
      window.bubsTrackCartError?.({
        http_status: error.httpStatus || 0,
        error_message: errorMessage || null,
        variant_id: cartItem?.id || null,
        quantity: cartItem?.quantity || null,
        purchase_type: cartItem?.selling_plan ? 'subscription' : 'one_time',
        selling_plan_id: cartItem?.selling_plan || null,
        source: 'recharge_widget',
      });
    })
    .finally(() => {
      submitButton.disabled = false;
      submitButton.classList.remove('loading');
    });
  }

  handleCartSuccess(data) {
    const cartElement = document.querySelector('cart-form');
    if (cartElement && cartElement._updateCart) {
      cartElement._updateCart(data.sections, 'open_drawer');
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    
    if (window.notificationEle) {
      window.notificationEle.updateNotification('Success', 'Item added to cart!', {
        type: 'success',
        timeout: 3000
      });
    }
  }

  showError(title, message) {
    if (window.notificationEle) {
      window.notificationEle.updateNotification(title, message, {
        type: 'error',
        timeout: 5000
      });
    } else {
      alert(`${title}: ${Array.isArray(message) ? message.join(', ') : message}`);
    }
  }

  // Cleanup method to clear intervals (for potential future use)
  destroy() {
    if (this.propertyPollInterval) {
      clearInterval(this.propertyPollInterval);
      this.propertyPollInterval = null;
    }
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new RechargeWidgetV2Integration());
} else {
  new RechargeWidgetV2Integration();
}
